/**
 * ARCHITECTURE — the recommended composite first, then the per-use-case diagrams.
 *
 * WHY THE IMAGES ARE SERVED FROM THIS DISTRIBUTION rather than linked to docs/ or GitHub:
 * the deployed CSP is `img-src 'self' data:`, so a cross-origin image is blocked at the edge and
 * renders as a broken image icon — which reads as a broken page. `web/build.mjs` copies the four
 * PNGs from docs/ into dist/architecture/ and FAILS THE BUILD if one is missing, so this panel
 * cannot silently render empty boxes.
 *
 * The recommended composite is expanded on arrival and the alternatives are collapsed. That is
 * the point of a recommendation: a reader who scrolls past should have seen the answer, not a
 * gallery of equally-weighted options.
 */

import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import { ARCH_BLUF, NO_OUTAGE_DRILL, RECLAMATION_OFF } from '../lib/caveats.js';

const IMG = 'architecture/';

/** A diagram at full panel width. Diagrams are wide, so they scale to the container. */
function Diagram({ file, alt, testid }) {
  return (
    <Box textAlign="center">
      <img
        src={`${IMG}${file}`}
        alt={alt}
        data-testid={testid}
        style={{
          maxWidth: '100%',
          height: 'auto',
          // A white plate behind the diagram. The PNGs are exported on white, so in dark mode a
          // transparent background leaves dark text on dark. Explicit rather than relying on the
          // export having an opaque background.
          background: '#ffffff',
          borderRadius: '8px',
          padding: '8px',
        }}
      />
    </Box>
  );
}

export default function ArchitecturePanel() {
  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header
            variant="h2"
            description="UC1 issuance plus UC4 rules. Two regions, active-active, with no cross-region call on the hot path."
          >
            Recommended architecture
          </Header>
        }
      >
        <SpaceBetween size="m">
          {/*
            THE BLUF, ABOVE THE DIAGRAM, BECAUSE THIS TAB OPENED STRAIGHT INTO A PICTURE.

            A technical reader opens Architecture first for orientation, and this panel told them
            WHICH option is recommended while saying nothing about why, or about what is still
            unmeasured — both of which were further down the same page, below the fold.

            ⚠️ IT SUMMARISES AND POINTS; IT IS NOT THE ONLY COPY. NO_OUTAGE_DRILL and
            RECLAMATION_OFF still render verbatim and in full below, in "What is not yet proven
            about this topology". The text is in lib/caveats.js rather than inline here for that
            file's ONE-COPY reason: a second, softer copy living in a panel would render perfectly
            and pass every assertion while saying less than the original.
          */}
          <Box variant="p" fontWeight="bold" data-testid="arch-bluf">
            {ARCH_BLUF}
          </Box>
          <Diagram
            file="recommended-architecture.png"
            alt="Recommended architecture: API Gateway and Lambda in two regions, each with its own ElastiCache Valkey buffer and DynamoDB high-water mark for serial issuance, plus Aurora DSQL active-active for the rules layer."
            testid="arch-recommended"
          />

          <ColumnLayout columns={3} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Serial issuance</Box>
              <Box>
                DynamoDB high-water mark + a shared ElastiCache Valkey buffer. A warm buffer serves
                a serial with zero DynamoDB calls.
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Rules</Box>
              <Box>
                Aurora DSQL, active-active in both regions. Each region accepts writes and sees the
                other’s immediately.
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Uniqueness</Box>
              <Box>
                Disjoint serial ranges per region. No replication, no Global Tables, no cross-region
                call on the vend path.
              </Box>
            </div>
          </ColumnLayout>

          <Box variant="p">
            <strong>The regions do not coordinate, and that is the design.</strong> Each region owns
            a disjoint half of every serial range, so two regions cannot issue the same tracking
            number even while partitioned from one another. Uniqueness is a property of the range
            split rather than of anything staying reachable — which is why a misrouted request is a
            latency cost and never a correctness risk.
          </Box>

          <Box variant="p">
            <strong>Replicating the high-water mark would break this, not strengthen it.</strong>{' '}
            DynamoDB Global Tables resolves conflicts last-writer-wins, so two regions advancing
            “the” mark would converge by discarding one of the two advances — and the discarded
            advance covers serials a region has already vended. That is duplicate tracking numbers,
            so Global Tables is deliberately off.
          </Box>

          <Alert type="info" header="What is not yet proven about this topology">
            <SpaceBetween size="xs">
              <Box variant="p">{NO_OUTAGE_DRILL}</Box>
              <Box variant="p">{RECLAMATION_OFF}</Box>
            </SpaceBetween>
          </Alert>
        </SpaceBetween>
      </Container>

      <Container
        header={
          <Header variant="h2" description="Each option as a standalone diagram, for comparison against the composite above.">
            The options, per use case
          </Header>
        }
      >
        <SpaceBetween size="m">
          <ExpandableSection headerText="UC1 — serial vending on DynamoDB (greenfield)" variant="footer">
            <SpaceBetween size="s">
              <Box variant="p">
                API Gateway and Lambda in front of a DynamoDB high-water mark, with a shared Valkey
                buffer absorbing the burst. No relational store, because a serial is a number and
                needs no joins.
              </Box>
              <Diagram
                file="greenfield-dynamodb-serial-vending.png"
                alt="UC1: API Gateway, Lambda, ElastiCache Valkey buffer and a DynamoDB high-water mark."
                testid="arch-uc1"
              />
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="UC2 — relational rules on Aurora PostgreSQL + a DynamoDB ledger" variant="footer">
            <SpaceBetween size="s">
              <Box variant="p">
                The same serial path, plus Aurora PostgreSQL for the part of the workload that is
                genuinely relational — which customer may use which service, which weight band
                applies, which rule wins — and a DynamoDB ledger for issued labels. Two stores,
                because the two datasets have opposite consistency and access needs.
              </Box>
              <Diagram
                file="relational-split-aurora-postgres-dynamodb.png"
                alt="UC2: the UC1 serial path plus Aurora PostgreSQL for rules and a DynamoDB ledger for issued labels."
                testid="arch-uc2"
              />
            </SpaceBetween>
          </ExpandableSection>

          <ExpandableSection headerText="UC4 — active-active rules on Aurora DSQL" variant="footer">
            <SpaceBetween size="s">
              <Box variant="p">
                UC2 with Aurora DSQL replacing Aurora PostgreSQL for the rules layer only. Serial
                vending is untouched. DSQL accepts writes in both regions, which Aurora Global
                Database cannot — but it enforces no foreign keys, so referential integrity moves
                into the application.
              </Box>
              <Diagram
                file="uc4-active-active-dsql-rules.png"
                alt="UC4: the UC1 serial path plus two Aurora DSQL clusters, each naming the other as a peer, for the rules layer."
                testid="arch-uc4"
              />
            </SpaceBetween>
          </ExpandableSection>

          {/*
            UC7 SITS LAST BECAUSE IT WAS MEASURED AND REJECTED, NOT BECAUSE IT IS LEAST BUILT.
            It is as deployed as UC1, UC2 and UC4 and it is the only option whose topology events
            were driven end to end. Ordering it after UC4 keeps the reading order chronological -
            UC4 is the recommendation and UC7 is the challenger that lost - but the header says
            "measured, not chosen" so nobody mistakes last place for unfinished.

            ⚠️ THE DIAGRAM'S JOB IS THE SINGLE-WRITER ASYMMETRY. UC4's diagram shows a thick
            BIDIRECTIONAL arrow between its clusters; this one shows a ONE-WAY arrow plus a dashed
            red forwarded write LEAVING us-west-2 to execute in us-east-1. Those two pictures side
            by side are the whole argument, which is why the tab was incomplete without this one:
            the recommendation rests on a comparison a reader previously could not see.
          */}
          <ExpandableSection
            headerText="UC7 — single-writer rules on Aurora PostgreSQL Global Database (measured, not chosen)"
            variant="footer"
          >
            <SpaceBetween size="s">
              <Box variant="p">
                UC2 with Aurora PostgreSQL Global Database replacing the single-region cluster for
                the rules layer only. Serial vending is untouched. Real foreign keys are enforced by
                the engine — the guarantee UC4 gives up — but there is exactly{' '}
                <strong>one writer</strong>. Write forwarding gives us-west-2 a write{' '}
                <strong>endpoint</strong>, not a writer: a write issued there is accepted locally and
                then carried to us-east-1 to execute, so it pays a cross-continent round trip and
                cannot complete at all if us-east-1 cannot serve.{' '}
                <strong>
                  What to look for: the replication arrow points ONE way, and a dashed red arrow
                  leaves us-west-2 to execute its write in us-east-1.
                </strong>
              </Box>
              <Box variant="p">
                This was built, deployed and driven through a live minor-version engine patch, a
                managed switchover and an <code>--allow-data-loss</code> failover with a caller
                watching in both regions. It wins the resolving read outright and lost no
                acknowledged write. It was not chosen because every topology event cost
                caller-visible read availability, and two of those flaws are intrinsic to a
                single-writer topology rather than fixable by configuration. See the UC7 tab for the
                measurements.
              </Box>
              <Diagram
                file="uc7-global-postgres-rules.png"
                alt="UC7: the UC1 serial path plus an Aurora PostgreSQL Global Database whose only writer is in us-east-1, replicating one way to a read-only secondary in us-west-2, with forwarded writes crossing back to the primary to execute."
                testid="arch-uc7"
              />
            </SpaceBetween>
          </ExpandableSection>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
